import React from 'react';

import { Input } from './Input';
import { Select } from './Select';
import RequiredBadge from './RequiredBadge';
import GoogleMapsAutocomplete from './GoogleMapsAutocomplete';
import { mergeDbConfigsWithDefaults, getDefaultFieldsForType } from '../Form/addressFieldConfig';

import { useStates } from '../../hooks';

/**
 * PlaceConfigurable - Configurable address component
 * Renders address fields based on field configuration from database
 */
const PlaceConfigurable = (props) => {
	const { data: states = [] } = useStates();

	const {
		fldType,
		label,
		onChange,
		updateAnswers,
		value,
		name,
		description,
		parent,
		isRequired = false,
		children,
		hasCounty = true,
		type_id,
		readOnly = false,
		fieldConfigs,  // Array of field_config records from database
		style,   // Form-level UI style settings
	} = props;

	// Get field configuration from database or use defaults
	let visibleFields = [];
	if (fieldConfigs && fieldConfigs.length > 0) {
		// Merge database configs with defaults
		const mergedFields = mergeDbConfigsWithDefaults(fieldConfigs, type_id);
		visibleFields = mergedFields.filter(f => f.visible).sort((a, b) => a.order - b.order);
	} else {
		// No configs in database, use type-based defaults
		const defaultFields = getDefaultFieldsForType(type_id);
		visibleFields = defaultFields.filter(f => f.visible).sort((a, b) => a.order - b.order);
	}

	// Check if addr_plus is enabled
	const isAddrPlusEnabled = fldType === "address_plus" || style?.addr_plus;

	// Group fields by their rendering groups
	const contactFields = visibleFields.filter(f => ['company', 'name', 'phone'].includes(f.key));
	const addressFields = visibleFields.filter(f => ['address', 'address_2'].includes(f.key));
	const addrPlusFields = visibleFields.filter(f => ['parcel_no', 'lot_no', 'block_no'].includes(f.key));
	const locationFields = visibleFields.filter(f => ['city', 'state', 'zip', 'county'].includes(f.key));
	const emailField = visibleFields.find(f => f.key === 'email');
	const bondField = visibleFields.find(f => f.key === 'bond_no');

	/**
	 * Render a single field based on its configuration
	 */
	const renderField = (field, index) => {
		const fieldName = parent + '.address.' + field.key;
		const fieldValue = value?.address?.[field.key];

		// Common props
		const commonProps = {
			key: field.key + '-' + index,
			label: field.label,
			name: fieldName,
			onChange: onChange,
			isRequired: field.required ? 1 : 0,
			value: fieldValue || '',
			placeholder: !style?.compact ? field.placeholder || field.label : null,
		};

		// Handle different field types
		switch (field.type) {
			case 'phone':
				return (
					<Input
						{...commonProps}
						type="tel"
						minLength={field.minLength || 7}
						maxLength={field.maxLength || 50}
						pattern={field.pattern}
					/>
				);

			case 'email':
				return (
					<Input
						{...commonProps}
						type="email"
						pattern={field.pattern || "[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$"}
					/>
				);

			case 'dropdown':
				if (field.key === 'state') {
					return (
						<Select
							{...commonProps}
							children={states.map((state, idx) => (
								<option key={idx} value={state.state_code}>
									{state.state_desc}
								</option>
							))}
						/>
					);
				}
				return <Input {...commonProps} type="text" />;

			case 'text':
			default:
				// Special handling for address field with Google Maps Autocomplete
				if (field.key === 'address') {
					return (
						<GoogleMapsAutocomplete
							{...commonProps}
							onSelect={(address) => {
								updateAnswers({
									[parent]: { address },
								});
							}}
						/>
					);
				}

				return (
					<Input
						{...commonProps}
						type="text"
						minLength={field.minLength}
						maxLength={field.maxLength}
					/>
				);
		}
	};

	/**
	 * Render fields in a row with proper column widths
	 */
	const renderFieldRow = (fields) => {
		if (!fields || fields.length === 0) return null;

		return (
			<div className="form-row">
				{fields.map((field, index) => (
					<div key={field.key} className={field.width || 'col'}>
						{renderField(field, index)}
						{field.helpText && (
							<small className="form-text text-muted">{field.helpText}</small>
						)}
					</div>
				))}
			</div>
		);
	};

	/**
	 * Render contact fields (company, name, phone)
	 */
	const printContactFields = () => {
		if (contactFields.length === 0) return null;

		// Group by rows based on width
		const companyField = contactFields.find(f => f.key === 'company');
		const otherContactFields = contactFields.filter(f => f.key !== 'company');

		return (
			<>
				{companyField && (
					<div className="form-row">
						<div className={companyField.width || 'col'}>
							{renderField(companyField, 0)}
							{companyField.helpText && (
								<small className="form-text text-muted">{companyField.helpText}</small>
							)}
						</div>
					</div>
				)}
				{otherContactFields.length > 0 && renderFieldRow(otherContactFields)}
			</>
		);
	};

	/**
	 * Render address fields (street address, address 2)
	 */
	const printAddressFields = () => {
		return addressFields.map((field, index) => (
			<div key={field.key} className="form-row">
				<div className={field.width || 'col'}>
					{renderField(field, index)}
					{field.helpText && (
						<small className="form-text text-muted">{field.helpText}</small>
					)}
				</div>
			</div>
		));
	};

	/**
	 * Render addr_plus fields (parcel, lot, block)
	 */
	const printAddrPlusFields = () => {
		if (!isAddrPlusEnabled || addrPlusFields.length === 0) return null;

		const parcelField = addrPlusFields.find(f => f.key === 'parcel_no');
		const otherFields = addrPlusFields.filter(f => f.key !== 'parcel_no');

		return (
			<>
				{parcelField && (
					<div className="form-row">
						<div className={parcelField.width || 'col'}>
							{renderField(parcelField, 0)}
							{parcelField.helpText && (
								<small className="form-text text-muted">{parcelField.helpText}</small>
							)}
						</div>
					</div>
				)}
				{otherFields.length > 0 && renderFieldRow(otherFields)}
			</>
		);
	};

	/**
	 * Render location fields (city, state, zip, county)
	 */
	const printLocationFields = () => {
		if (locationFields.length === 0) return null;

		// Filter county if hasCounty is false
		const fieldsToRender = hasCounty 
			? locationFields 
			: locationFields.filter(f => f.key !== 'county');

		return renderFieldRow(fieldsToRender);
	};

	/**
	 * Render email field
	 */
	const printEmailField = () => {
		if (!emailField) return null;

		return (
			<div className="form-row">
				<div className={emailField.width || 'col'}>
					{renderField(emailField, 0)}
					{emailField.helpText && (
						<small className="form-text text-muted">{emailField.helpText}</small>
					)}
				</div>
			</div>
		);
	};

	/**
	 * Render bond number field (for bonding companies)
	 */
	const printBondField = () => {
		if (!bondField) return null;

		return (
			<div className="form-row">
				<div className={bondField.width || 'col'}>
					{renderField(bondField, 0)}
					{bondField.helpText && (
						<small className="form-text text-muted">{bondField.helpText}</small>
					)}
				</div>
			</div>
		);
	};

	// Editable mode
	if (!readOnly) {
		return (
			<div className={'form-group address_section_' + (type_id ? type_id : '0') + (style?.compact ? ' compact' : '')}>
				<fieldset className={style?.fieldset ? 'show' : 'hide'}>
					<legend className={style?.fieldset ? 'show' : 'hide'}>{label}</legend>

					{description && (
						<div className="form-row">
							<div className="col">
								<div className="input--description">
									{description}
								</div>
							</div>
						</div>
					)}

					{bondField && printBondField()}

					<div className="row">
						{style?.layout === 'columns' ? (
							<>
								<div className="col">
									{printContactFields()}
									{printEmailField()}
								</div>
								<div className="col">
									{printAddressFields()}
									{printAddrPlusFields()}
									{printLocationFields()}
								</div>
							</>
						) : (
							<div className="col">
								{printContactFields()}
								{printAddressFields()}
								{printAddrPlusFields()}
								{printLocationFields()}
								{printEmailField()}
							</div>
						)}
					</div>
					{children}
				</fieldset>
			</div>
		);
	}

	// Read-only mode
	const addr = value['address'];
	
	// Check if address is empty
	const isAddressEmpty = Object.keys(addr).every(key => 
		!addr[key] || addr[key] === ''
	);

	if (isAddressEmpty) {
		return <div>No Address Data Found</div>;
	}

	// Build address display based on visible fields
	let address = [];
	
	visibleFields.forEach(field => {
		const fieldValue = addr[field.key];
		if (fieldValue && fieldValue !== '') {
			// Special formatting for city, state, zip combo
			if (field.key === 'city' && addr.state && addr.zip) {
				address.push(`${addr.city}, ${addr.state} ${addr.zip}`);
				// Skip state and zip since they're already included
			} else if (field.key === 'state' || field.key === 'zip') {
				// Skip if already handled in city combo
				const cityField = visibleFields.find(f => f.key === 'city');
				if (!cityField || !addr.city) {
					address.push(fieldValue);
				}
			} else {
				address.push(fieldValue);
			}
		}
	});

	return (
		<div className="form-group">
			<fieldset className={style?.fieldset ? 'show' : 'hide'}>
				<legend className={style?.fieldset ? 'show' : 'hide'}>{label}</legend>
				
				{addr?.bond_no && (
					<div className="form-row">
						<div className="col">
							<label>Bond Number{' '}</label>
							<span>{addr?.bond_no}</span>
						</div>
					</div>
				)}
				
				<div className="form-row">
					<div className="col">
						<label style={{ verticalAlign: 'top' }}>
							Address {isRequired === 1 && <RequiredBadge value={address?.length > 0} />}{' '}
						</label>
						<span style={{ display: 'inline-block', whiteSpace: 'pre-wrap' }}>
							{address.map((addr_field, idx) => addr_field + (idx < address.length - 1 ? '\n' : ''))}
						</span>
					</div>
				</div>
			</fieldset>
		</div>
	);
};

export default PlaceConfigurable;


